/**
 * Wording for a media-capture failure — one place, so the two screens that show
 * the modal and the toast that reports it cannot drift apart.
 *
 * Every string follows the device that was actually asked for. They all used to
 * say "camera": a voice consultation, which asks for no camera at all, told the
 * customer "Camera Not Responding" while the browser dialog behind the modal was
 * asking them about microphones.
 */

import type { MediaError, MediaErrorType, MediaRequest } from '../providers/types';

/**
 * The device the message should talk about. Falls back to naming both for an
 * error raised before anyone recorded what was asked for.
 */
export function requestedOf(error: MediaError): MediaRequest {
  return error.requested ?? 'both';
}

/** Capitalised subject of a headline: "Camera", "Microphone", "Camera & Microphone". */
function subject(requested: MediaRequest): string {
  switch (requested) {
    case 'camera': return 'Camera';
    case 'microphone': return 'Microphone';
    case 'both': return 'Camera & Microphone';
  }
}

/** Same devices, in the middle of a sentence. */
function devices(requested: MediaRequest): string {
  switch (requested) {
    case 'camera': return 'camera';
    case 'microphone': return 'microphone';
    case 'both': return 'camera and microphone';
  }
}

/**
 * What the customer keeps when they give up on the device.
 *
 * Without a microphone there is no voice call to fall back to, so "continue
 * without video" would be offering something that does not exist — chat is the
 * floor. The callers already fall through to chat in that case; this is the
 * label catching up with the behaviour.
 */
export function continueLabel(requested: MediaRequest): string {
  return requested === 'microphone' ? 'Continue in chat' : 'Continue without video';
}

export function getMediaErrorTitle(type: MediaErrorType, requested: MediaRequest = 'both'): string {
  const device = subject(requested);
  switch (type) {
    case 'permission-denied': return `${device} Access Denied`;
    case 'permission-dismissed': return `Allow ${device} Access`;
    case 'device-not-found': return `${device} Not Found`;
    case 'device-in-use': return `${device} In Use`;
    case 'overconstrained': return `${device} Settings Error`;
    case 'capture-timeout': return `${device} Not Responding`;
    case 'security': return 'Secure Connection Required';
    case 'network': return 'Connection Failed';
    case 'unknown': return 'Call Unavailable';
  }
}

export function getMediaErrorBody(
  type: MediaErrorType,
  requested: MediaRequest = 'both',
  options: { deniedBySystem?: boolean } = {}
): string {
  const device = devices(requested);
  const fallback = requested === 'microphone'
    ? 'You can continue in chat'
    : 'You can continue without video';
  switch (type) {
    case 'permission-dismissed':
      // Nothing is blocked: the dialog was closed, and the browser will show it
      // again. Settings instructions here would send the customer to fix a block
      // that does not exist — and every closed dialog brings Chrome's real block
      // (three closures) one step nearer.
      return `You closed the browser's request for ${device} access. Press Try again and choose Allow. ${fallback} instead.`;
    case 'permission-denied':
      if (options.deniedBySystem) {
        return `Your computer's privacy settings are blocking ${device} access for your browser. ${fallback}, or allow your browser in the system settings and try again.`;
      }
      // Deliberately does NOT offer "try a different device": the block is on the
      // site, not on the camera, so every device fails the same way. Pointing at
      // a second camera here is what made the demo modal self-contradictory.
      return `Your browser is blocking ${device} access for this page. ${fallback}, or unblock access and try again.`;
    case 'device-not-found':
      if (requested === 'microphone') {
        return 'No microphone was found. You can continue in chat instead.';
      }
      return requested === 'both'
        ? 'No camera or microphone was found. You can continue with audio or chat instead.'
        : 'No camera was found. You can continue with audio or chat instead.';
    case 'device-in-use':
      return `Your ${device} could not be started — another application is holding it. ${requested === 'microphone' ? 'Close the other app and try again, or continue in chat.' : 'Pick a different camera, close the other app and try again, or continue without video.'}`;
    case 'overconstrained':
      return `The selected ${device} does not support the requested settings. ${fallback}, or try a different device.`;
    case 'capture-timeout':
      return `Your ${device} did not respond in time. It may be in use or temporarily unavailable. You can try again${requested === 'microphone' ? ' or continue in chat.' : ' or continue without video.'}`;
    case 'security':
      return `${subject(requested)} access requires a secure (HTTPS) connection.`;
    case 'network':
    case 'unknown':
      return 'We were unable to start your call. You can continue with audio or chat instead.';
  }
}
